import {
  IconBrandAdobe,
  IconBrandAirbnb,
  IconBrandAmazon,
  IconBrandApple,
  IconBrandBooking,
  IconBrandDiscord,
  IconBrandEtsy,
  IconBrandFigma,
  IconBrandGithub,
  IconBrandGoogle,
  IconBrandLinkedin,
  IconBrandMeta,
  IconBrandNetflix,
  IconBrandNotion,
  IconBrandOpenai,
  IconBrandPaypal,
  IconBrandShopee,
  IconBrandSlack,
  IconBrandSpotify,
  IconBrandSteam,
  IconBrandTiktok,
  IconBrandUber,
  IconBrandWindows,
  IconBrandX,
  IconBrandZoom,
} from "@tabler/icons-react";
import * as React from "react";

import { cn } from "@/shared/lib/utils";
import { merchantBrandOf } from "@/shared/utils/merchantBrands";

type BrandIcon = typeof IconBrandMeta;

// Brands with a mark in the icon set; the rest get their initial on their colour.
const BRAND_ICONS: Record<string, BrandIcon> = {
  Meta: IconBrandMeta,
  Google: IconBrandGoogle,
  Amazon: IconBrandAmazon,
  Apple: IconBrandApple,
  Microsoft: IconBrandWindows,
  LinkedIn: IconBrandLinkedin,
  OpenAI: IconBrandOpenai,
  Uber: IconBrandUber,
  Netflix: IconBrandNetflix,
  Spotify: IconBrandSpotify,
  TikTok: IconBrandTiktok,
  X: IconBrandX,
  Airbnb: IconBrandAirbnb,
  "Booking.com": IconBrandBooking,
  Adobe: IconBrandAdobe,
  Zoom: IconBrandZoom,
  Notion: IconBrandNotion,
  PayPal: IconBrandPaypal,
  Shopee: IconBrandShopee,
  Etsy: IconBrandEtsy,
  Slack: IconBrandSlack,
  Discord: IconBrandDiscord,
  GitHub: IconBrandGithub,
  Figma: IconBrandFigma,
  Steam: IconBrandSteam,
};

export interface ColocaMerchantMarkProps extends Omit<React.ComponentPropsWithoutRef<"span">, "children"> {
  /** The merchant as the card network names it ("FACEBK *ADS 1234"), or a brand name ("Meta"). */
  merchant: string | null | undefined;
  /**
   * What an unknown merchant shows: its initial on a neutral tile, or nothing (the component renders `null`,
   * so the caller keeps its own icon).
   */
  fallback?: "initial" | "none";
  size?: "sm" | "md";
}

/**
 * The mark of a card merchant: the brand's logo on a tile of its own colour when the name is a known brand
 * (`merchantBrandOf`), its initial on that colour when the brand has no logo in the icon set, and for any
 * other merchant its initial on a neutral tile or nothing. Decorative: the merchant's name is always written
 * next to it.
 */
export const ColocaMerchantMark = React.forwardRef<HTMLSpanElement, ColocaMerchantMarkProps>(
  function ColocaMerchantMark(
    { merchant, fallback = "initial", size = "md", className, style, ...rest },
    ref,
  ) {
    const brand = merchantBrandOf(merchant);
    if (!brand && fallback === "none") return null;

    const Icon = brand ? BRAND_ICONS[brand.name] : undefined;
    const initial = (brand?.name ?? merchant ?? "").trim().charAt(0).toUpperCase() || "·";
    const color = brand?.color ?? null;

    return (
      <span
        ref={ref}
        aria-hidden="true"
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-[var(--radius-md)] font-semibold",
          size === "sm" ? "size-7 text-xs" : "size-9 text-sm",
          color
            ? "bg-[color-mix(in_oklab,var(--merchant-mark)_14%,var(--card))] text-[var(--merchant-mark)] dark:bg-[color-mix(in_oklab,var(--merchant-mark)_24%,var(--card))] dark:text-[color-mix(in_oklab,var(--merchant-mark)_45%,var(--foreground))]"
            : "bg-muted text-foreground",
          className,
        )}
        style={color ? ({ "--merchant-mark": color, ...style } as React.CSSProperties) : style}
        {...rest}
      >
        {Icon ? <Icon className={size === "sm" ? "size-4" : "size-5"} stroke={1.75} /> : initial}
      </span>
    );
  },
);
